import { Dialog, DialogContent, Button, Grid, IconButton, Popover, Typography } from '@mui/material';
import { Info } from '@mui/icons-material';
import { CloseIconButton, fetchFile, useWindowDimensions, } from '../Utilities';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Document, Page } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

// callers gate the trigger when the file is missing — alert UI was removed once both call sites adopted disabled-button + inline alert
export default function PdfDialog({ open, setOpen, fileExists, filePath, targetFilename }) {

    const { CAMERAS } = window.conf;
    const { t } = useTranslation();
    const { contentHeight, windowWidth } = useWindowDimensions()

    const [anchorPopover, setAnchorPopover] = useState(null);
    const [numPages, setNumPages] = useState(null);

    const moreThanOneCam = CAMERAS.length > 1
    const openPopover = Boolean(anchorPopover)
    const idPopover = openPopover ? 'simple-popover' : undefined
    const maxWindowWidth = 900
    const contentOffset = {
        height: 3 * 16 + 56 + (moreThanOneCam && 72), // 3 * 16px padding + 56px button height and tolerance + 72px camera selector
        width: 2 * 16 + (windowWidth > maxWindowWidth ? 24 : 84) // 2 * 16px padding + 24px or 84px tolerance
    }

    // disables panning when image is not zoomed in or panned
    const PanningInteractionHandler = (instance, state) => {
        if (state?.positionX >= 0 && state?.positionY >= 0 && state?.scale <= 1) {
            instance.setup.panning.disabled = true;
        } else {
            instance.setup.panning.disabled = false;
        }
    }

    const pdfDocument = (
        <Document 
            file={filePath}
            loading={t('actions.loading')}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            error={t('msg.exe_error')}
            onLoadError={error => { console.error(`[Diary] Failed to load PDF:`, error); setNumPages(0); }}
            noData={t('msg.no_data')}
            // onLoadProgress={({ loaded, total }) => console.log('Loading a document: ' + (loaded / total) * 100 + '%')}
        >

            {numPages > 0 && Array.from({ length: numPages }, (_, index) => (
                <Page
                    key={index + 1}
                    pageNumber={index + 1}
                    width={(windowWidth > maxWindowWidth ? maxWindowWidth : windowWidth) - contentOffset.width}
                />
            ))}

        </Document>
    )

    const onCloseDialog = () => {
        setOpen(false)
    }

    const onOpenPopover = (event) => {
        setAnchorPopover(event.currentTarget)
    }
    
    const onClosePopover = () => {
        setAnchorPopover(null)
    }

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onCloseDialog}
                sx={{ "& .MuiDialog-paper": { maxWidth: windowWidth > maxWindowWidth ? maxWindowWidth : windowWidth }}}
            >
                <CloseIconButton onClick={onCloseDialog} />

                <DialogContent sx={{ p: 2 }}>

                    <Grid container>

                        <Grid item xs={12} sx={{ pb: 0 }} /> {/* An empty grid item. A trick to fix the lable layout issue on FormControl */}

                        {fileExists && (
                            <Grid 
                                item 
                                xs={12} 
                                sx={{ 
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: 'rgba(196,196,196)',
                                    borderRadius: '4px',
                                    height: contentHeight - contentOffset.height,
                                    overflow: 'auto'
                                }}
                            >

                                {isMobile ? (
                                    <TransformWrapper
                                        onZoomStop={ref => PanningInteractionHandler(ref.instance, ref.state)}
                                        onPanning={ref => PanningInteractionHandler(ref.instance, ref.state)}
                                        onInit={ref => ref.instance.setup.panning.disabled = true}
                                        disablePadding // removes paddable border around the content
                                    >
                                        <TransformComponent>
                                            {pdfDocument}
                                        </TransformComponent>
                                    </TransformWrapper> 
                                ) : (
                                    pdfDocument
                                )}

                            </Grid>
                        )}

                        <Grid
                            item 
                            xs={12}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Button onClick={onCloseDialog} variant="outlined">
                                {t('actions.close')}
                            </Button>

                            <IconButton
                                aria-label={idPopover}
                                onClick={onOpenPopover}
                                color="primary"
                            >
                                <Info />
                            </IconButton>
                                
                            <Button 
                                variant="outlined" 
                                onClick={() => fetchFile(filePath, `${targetFilename}.pdf`)}
                                disabled={!fileExists || !numPages}
                            >
                                {t('actions.download')}
                            </Button>

                        </Grid>

                    </Grid>
                </DialogContent>
            </Dialog>

            <Popover
                id={idPopover}
                open={openPopover}
                anchorEl={anchorPopover}
                onClose={onClosePopover}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
            >
                <Typography sx={{ 
                    p: 2, 
                    whiteSpace: "pre-line", 
                    userSelect: "none" 
                }}>
                    {t('events.ai')}
                </Typography>
            </Popover>
        </>
    )
}